import React, { useState, useEffect } from 'react'
import QueueService from './QueueService'
import './RestrictReservations.css'

// 0 = Monday … 6 = Sunday
const WEEK_DAYS = [
  { label:'Mon', value:0 },
  { label:'Tue', value:1 },
  { label:'Wed', value:2 },
  { label:'Thu', value:3 },
  { label:'Fri', value:4 },
  { label:'Sat', value:5 },
  { label:'Sun', value:6 },
]

export default function RestrictReservations({token, business_id }) {
  const [mode, setMode]        = useState('view')    // 'view' | 'edit'
  const [tab, setTab]          = useState('date')    // 'date' | 'weekly'
  const [dateGroups, setDateGroups]     = useState([])
  const [weeklyGroups, setWeeklyGroups] = useState([])
  const [newWeeklyDay, setNewWeeklyDay] = useState('')
  const [minAdvance, setMinAdvance]     = useState(0)
  const [maxAdvance, setMaxAdvance]     = useState(0)
  const [origRules, setOrigRules]       = useState(null)
  const [otherMetadata, setOtherMetadata] = useState({})

  // days still available to add
  const availableDays = WEEK_DAYS.filter(
    w => !weeklyGroups.some(g => g.day === w.value)
  )

  useEffect(() => {
    async function load() {
      if (!token) return;
      try {
        const resp = await QueueService.GetBusinessInfo(business_id)
        const rules = resp.business_metadata.reservation_rules || {}
        const { reservation_rules, ...rest } = resp.business_metadata
        setOrigRules(rules)
        setOtherMetadata(rest)
        hydrateFromRules(rules)
      } catch (err) {
        console.error(err)
        alert('Failed to load')
      }
    }
    load()
  }, [business_id, token])

  function hydrateFromRules(r) {
    // DATE GROUPS
    const dg = []
    ;(r.date_rules || []).forEach(rule => {
      const full = rule.time_range === null
      let grp = dg.find(g => g.date===rule.date && g.fullDay===full)
      if (!grp) {
        grp = { date:rule.date, fullDay:full, slots:[] }
        dg.push(grp)
      }
      if (!full && rule.time_range) {
        grp.slots.push({
          start: rule.time_range.start.slice(0,5),
          end:   rule.time_range.end.slice(0,5)
        })
      }
    })
    if (!dg.length) dg.push({ date:'', fullDay:false, slots:[{start:'08:00',end:'17:00'}] })

    // WEEKLY GROUPS: one entry per day
    const wg = []
    ;(r.weekly_rules || []).forEach(rule => {
      const full = rule.time_range === null
      rule.days_of_week.forEach(dow => {
        const slots = full
          ? []
          : [{ start: rule.time_range.start.slice(0,5),
               end:   rule.time_range.end.slice(0,5) }]
        wg.push({ day:dow, fullDay:full, slots })
      })
    })
    if (!wg.length) wg.push({ day:null, fullDay:false, slots:[{start:'08:00',end:'17:00'}] })

    setDateGroups(dg)
    setWeeklyGroups(wg)
    setMinAdvance(r.min_advance_booking_days || 0)
    setMaxAdvance(r.max_advance_booking_days || 0)
  }

  function buildPayload() {
    const date_rules = dateGroups.flatMap(g => {
      if (!g.date) {
        return []
      }
      if (g.fullDay || !g.slots.length) {
        return [{ date:g.date, time_range:null, allowed:false }]
      }
      return g.slots.map(s => ({
        date:g.date,
        time_range:{ start:`${s.start}:00`, end:`${s.end}:00` },
        allowed:false
      }))
    })

    const weekly_rules = weeklyGroups.flatMap(g => {
      if (g.fullDay || !g.slots.length) {
        return [{ days_of_week:[g.day], time_range:null, allowed:false }]
      }
      return g.slots.map(slot => ({
        days_of_week: [g.day],
        time_range: { start: `${slot.start}:00`, end: `${slot.end}:00` },
        allowed: false
      }))
    })

    return {
      date_rules,
      weekly_rules,
      min_advance_booking_days: minAdvance,
      max_advance_booking_days: maxAdvance
    }
  }

  // Handlers…
  const updateDateGroup = (i, fn) =>
    setDateGroups(dg => dg.map((g,idx)=> idx===i?fn(g):g))

  const addDateGroup = () =>
    setDateGroups(dg=>[...dg,{ date:'', fullDay:false, slots:[{start:'08:00',end:'17:00'}] }])

  const removeDateGroup = i =>
    setDateGroups(dg=>dg.filter((_,idx)=>idx!==i))

  const updateWeeklyGroup = (i, fn) =>
    setWeeklyGroups(wg => wg.map((g,idx)=> idx===i?fn(g):g))

  const removeWeeklyGroup = i =>
    setWeeklyGroups(wg => wg.filter((_,idx)=>idx!==i))

  const addNewWeekly = () => {
    if(!newWeeklyDay) return
    setWeeklyGroups(wg => [
      ...wg,
      { day:+newWeeklyDay, fullDay:false, slots:[{start:'08:00',end:'17:00'}] }
    ])
    setNewWeeklyDay('')
  }

  async function onSave() {
    const rules = buildPayload()
    const payload = {
      ...otherMetadata,
      reservation_rules: rules
    }
    try {
      await QueueService.UpdateBusinessInfo(payload, business_id, token)
      setOrigRules(rules)
      setMode('view')
      alert('Saved')
    } catch (err) {
      console.error(err)
      alert('Save failed')
    }
  }

  function onCancel() {
    origRules && hydrateFromRules(origRules)
    setMode('view')
  }

  // Render
  if (mode === 'view') {
    const formatTime = (time24) => {
      const [hour, minute] = time24.split(':').map(Number);
      const ampm = hour >= 12 ? 'PM' : 'AM';
      const hour12 = hour % 12 === 0 ? 12 : hour % 12;
      return `${hour12}:${minute.toString().padStart(2, '0')} ${ampm}`;
    };
  
    const formatTimeRange = (range) => {
      if (!range) return 'Full Day';
      return `${formatTime(range.start)} to ${formatTime(range.end)}`;
    };
  
    return (
      <div className="restrict-reservations-container-view">
        <h2>Reservation Restriction Rules</h2>
  
        <section>
          <h4>Booking not allowed on:</h4>
          {origRules?.date_rules?.map((r, i) => (
            <div key={i} className="view-only-slot">
              <div className="day">{r.date}</div>
              <div className="time">{formatTimeRange(r.time_range)}</div>
            </div>
          ))}
        </section>
  
        <section>
          <h4>Booking not allowed on:</h4>
          {origRules?.weekly_rules?.map((r, i) => (
            <div key={i} className="view-only-slot">
              <div className="day">
                {r.days_of_week
                  .map(d => WEEK_DAYS.find(w => w.value === d)?.label)
                  .join(', ')}
              </div>
              <div className="time">{formatTimeRange(r.time_range)}</div>
            </div>
          ))}
        </section>
  
        {origRules && (
  <section className="advance-booking-allowed-view">
    <h3>Booking Allowed Period</h3>
    <div className="view-only-slot-allowed">
      <span className="label-allowed">Booking start date:</span>
      <span className="value-allowed">
        {origRules.min_advance_booking_days === 0
          ? "Today"
          : origRules.min_advance_booking_days === 1
          ? "Tomorrow"
          : `${origRules.min_advance_booking_days} days from today`}
      </span>
      <span className="separator-allowed">|</span>
      <span className="label-allowed">Booking end date:</span>
      <span className="value-allowed">
        {origRules.max_advance_booking_days} day
        {origRules.max_advance_booking_days > 1 ? "s" : ""} from today
      </span>
    </div>
  </section>
)}




  
        <button onClick={() => setMode('edit')}>Edit Rules</button>
      </div>
    );
  }
  

  // EDIT MODE
  return (
    <div className="restrict-reservations-container">

      <div className="mode-switch">
        <button className={tab==='date'?'active':''}   onClick={()=>setTab('date')}>By Date</button>
        <button className={tab==='weekly'?'active':''} onClick={()=>setTab('weekly')}>By Days of Week</button>
      </div>

      {tab==='date' && dateGroups.map((g,i)=>(
        <div className="date-group" key={i}>
          <input
            type="date"
            value={g.date}
            onChange={e=>updateDateGroup(i,d=>({...d,date:e.target.value}))}
          />
          <label>
            <input
              type="checkbox"
              checked={g.fullDay}
              onChange={e=>updateDateGroup(i,d=>({
                ...d,
                fullDay: e.target.checked,
                slots: e.target.checked?[]: d.slots.length?d.slots:[{start:'08:00',end:'17:00'}]
              }))}
            />
            Full Day
          </label>

          {!g.fullDay && g.slots.map((s,si)=>(
            <div className="slot-row" key={si}>
              <input
                type="time"
                value={s.start}
                onChange={e=>updateDateGroup(i,d=>({
                  ...d,
                  slots:d.slots.map((sl,idx)=> idx===si?{...sl,start:e.target.value}:sl)
                }))}
              />
              <input
                type="time"
                value={s.end}
                onChange={e=>updateDateGroup(i,d=>({
                  ...d,
                  slots:d.slots.map((sl,idx)=> idx===si?{...sl,end:e.target.value}:sl)
                }))}
              />
              <button onClick={()=>updateDateGroup(i,d=>({
                ...d,
                slots:d.slots.filter((_,idx)=>idx!==si)
              }))}>🗑</button>
            </div>
          ))}

          {!g.fullDay && (
            <button className="add-slot" onClick={()=>updateDateGroup(i,d=>({
              ...d, slots:[...d.slots,{start:'08:00',end:'17:00'}]
            }))}>+ Add Slot</button>
          )}

          <button className="remove-group" onClick={()=>removeDateGroup(i)}>
            Remove Date
          </button>
        </div>
      ))}

      {tab==='date' && (
        <button className="add-group" onClick={addDateGroup}>
          + Add Another Date
        </button>
      )}

      {tab==='weekly' && weeklyGroups.map((g,i)=>(
        <div className="weekly-group" key={i}>
          <div className="weekly-header">
            <strong>{WEEK_DAYS.find(w=>w.value===g.day)?.label}</strong>
            <label>
              <input
                type="checkbox"
                checked={g.fullDay}
                onChange={e=>updateWeeklyGroup(i,d=>({
                  ...d,
                  fullDay:e.target.checked,
                  slots:e.target.checked?[]: d.slots.length?d.slots:[{start:'08:00',end:'17:00'}]
                }))}
              />
              Closed All Day
            </label>
          </div>

          {!g.fullDay && g.slots.map((s,si)=>(
            <div className="slot-row" key={si}>
              <input
                type="time"
                value={s.start}
                onChange={e=>updateWeeklyGroup(i,d=>({
                  ...d,
                  slots:d.slots.map((sl,idx)=> idx===si?{...sl,start:e.target.value}:sl)
                }))}
              />
              <input
                type="time"
                value={s.end}
                onChange={e=>updateWeeklyGroup(i,d=>({
                  ...d,
                  slots:d.slots.map((sl,idx)=> idx===si?{...sl,end:e.target.value}:sl)
                }))}
              />
              <button onClick={()=>updateWeeklyGroup(i,d=>({
                ...d, slots:d.slots.filter((_,idx)=>idx!==si)
              }))}>🗑</button>
            </div>
          ))}

          {!g.fullDay && (
            <button className="add-slot" onClick={()=>updateWeeklyGroup(i,d=>({
              ...d, slots:[...d.slots,{start:'08:00',end:'17:00'}]
            }))}>+ Add Slot</button>
          )}

          <button className="remove-group" onClick={()=>removeWeeklyGroup(i)}>
            Remove Rule
          </button>
        </div>
      ))}

      {tab==='weekly' && availableDays.length>0 && (
        <div className="add-weekly-group">
          <select
            value={newWeeklyDay}
            onChange={e=>setNewWeeklyDay(e.target.value)}
          >
            <option value="">Add another day…</option>
            {availableDays.map(w=>(
              <option key={w.value} value={w.value}>{w.label}</option>
            ))}
          </select>
          <button onClick={addNewWeekly}>Add Day</button>
        </div>
      )}

      <div className="advance-days">
        <label>
          Min advance days
          <input
            type="number" min="0"
            value={minAdvance}
            onChange={e=>setMinAdvance(parseInt(e.target.value)||0)}
          />
        </label>
        <label>
          Max advance days
          <input
            type="number" min="0"
            value={maxAdvance}
            onChange={e=>setMaxAdvance(parseInt(e.target.value)||0)}
          />
        </label>
      </div>

      <div className="form-actions">
        <button className="save-button"   onClick={onSave}>Save</button>
        <button className="cancel-button" onClick={onCancel}>Cancel</button>
      </div>
    </div>
  )
}
